import L from "leaflet";
import "../assets/NewFiles/Css/ProjectPage.css";
import React, { useEffect, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import { Gallery } from "react-grid-gallery";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { FaTree, FaBullseye, FaHashtag, FaFilePdf } from "react-icons/fa";
import api from "../axios/api";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
} from "react-leaflet";
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaRegClock,
} from "react-icons/fa";
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
} from "react-share";
import {
  EmailIcon,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from "react-share";
import {
  FaInstagram,
} from "react-icons/fa";
import MapToggle from "../components/common/MapToggle";
import { Tooltip } from "react-tooltip";
import MapSpeciesFilter from "../components/projectPage/MapSpeciesFilter";
import CustomClusterGroup from "../components/projectPage/CustomClusterGroup";
import noImage from "../assets/images/noImage.jpg";
import Loader from "../components/common/Loader";
import ReactLoading from "react-loading";


// Fix for default marker icons in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

const mockData = {
  treesByMonth: {
    labels: [
      "2021-1",
      "2021-6",
      "2021-11",
      "2022-4",
      "2022-9",
      "2023-2",
      "2023-7",
      "2023-12",
      "2024-5",
    ],
    data: [300, 3200, 700, 900, 800, 400, 300, 500, 200],
  },
  co2OffsetByMonth: {
    labels: [
      "2021-1",
      "2021-6",
      "2021-11",
      "2022-4",
      "2022-9",
      "2023-2",
      "2023-7",
      "2023-12",
      "2024-5",
    ],
    data: [20, 40, 80, 120, 160, 220, 300, 380, 460],
  },
  position: [20.5937, 78.9629],
  averageWeeklyTrees: {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    data: [120, 150, 180, 130, 160, 90, 110],
  },
};

const ProjectPage = () => {
  const { projectid } = useParams();
  const [projectData, setProjectData] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [treeMapData, setTreeMapData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [apiStats, setApiStats] = useState([]);
  const [geoJsonData, setGeoJsonData] = useState(null);

  const [speciesData, setSpeciesData] = useState({});
  const [filteredTreeMapData, setFilteredTreeMapData] = useState([]);

  const [isExporting, setIsExporting] = useState(false);
  const co2ChartRef = React.useRef(null);
  const growthChartRef = React.useRef(null);

  const DEFAULT_WOOD_DENSITY = 0.5;
  const DEFAULT_FORM_FACTOR = 0.5;
  const DBH_GROWTH_PER_MONTH = 0.05 / 100;
  const SEEDLING_DBH = 0.005;
  const [co2ChartData, setCo2ChartData] = useState(null);
  const [totalCO2e, setTotalCO2e] = useState(0);
  const [treesWithDBH, setTreesWithDBH] = useState(0);

  const CO2_TO_O2_RATIO = 0.727; // O2(kg) = CO2e(kg) × 32/44 — mass ratio from photosynthesis stoichiometry
  const [totalO2, setTotalO2] = useState(0);

  // Add near the top of the component
  const getSpeciesLabel = (tree) =>
    tree.specie_common_name && tree.tree_species_label
      ? `${tree.specie_common_name} / ${tree.tree_species_label}`
      : tree.specie_common_name || tree.tree_species_label || tree.treeName || `Species-${tree.tree_species}` || 'Unknown Species';

  const calcCO2eFromDBH = (dbh_meters, wd = DEFAULT_WOOD_DENSITY, ff = DEFAULT_FORM_FACTOR) => {
    try {
      if (!dbh_meters || isNaN(dbh_meters) || parseFloat(dbh_meters) <= 0) return 0;
      const D = parseFloat(dbh_meters) * 100;
      const WD = (wd && !isNaN(wd) && parseFloat(wd) > 0) ? parseFloat(wd) : DEFAULT_WOOD_DENSITY;
      const FF = (ff && !isNaN(ff) && parseFloat(ff) > 0) ? parseFloat(ff) : DEFAULT_FORM_FACTOR;
      const lnD = Math.log(D);
      const AGB = FF * WD * Math.exp(
        -1.499 + 2.148 * lnD + 0.207 * lnD ** 2 - 0.0281 * lnD ** 3
      );
      return parseFloat((AGB * 0.47 * 3.67).toFixed(4));
    } catch {
      return 0;
    }
  };

  const buildCO2ChartData = (trees) => {
    if (!trees || trees.length === 0) return;

    const now = new Date();

    let globalStart = null;
    trees.forEach(tree => {
      const d = tree.plantedOn || tree.createdAt;
      if (!d) return;
      const date = new Date(d);
      if (!globalStart || date < globalStart) globalStart = date;
    });

    if (!globalStart) return;

    const start = new Date(globalStart.getFullYear(), globalStart.getMonth(), 1);

    const labels = [];
    let cursor = new Date(start);
    while (cursor <= now) {
      labels.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const monthlySums = new Array(labels.length).fill(0);

    trees.forEach(tree => {
      const treeStartDate = tree.plantedOn || tree.createdAt;
      if (!treeStartDate) return;

      const treeStart = new Date(treeStartDate);
      const knownDBH = tree.dia_breast_height != null && tree.dia_breast_height !== "null"
        ? parseFloat(tree.dia_breast_height) : null;
      const wd = tree.bd_biomass_density;
      const ff = tree.form_factor;

      labels.forEach((label, i) => {
        const [yr, mo] = label.split("-").map(Number);
        const monthDate = new Date(yr, mo - 1, 1);

        if (monthDate < new Date(treeStart.getFullYear(), treeStart.getMonth(), 1)) return;

        let dbh;
        const monthsFromPlanting = (monthDate - treeStart) / (1000 * 60 * 60 * 24 * 30.44);

        if (knownDBH) {
          const totalMonths = (now - treeStart) / (1000 * 60 * 60 * 24 * 30.44);
          if (monthDate <= now) {
            const progress = totalMonths > 0 ? monthsFromPlanting / totalMonths : 1;
            dbh = Math.max(SEEDLING_DBH, SEEDLING_DBH + progress * (knownDBH - SEEDLING_DBH));
          } else {
            const monthsAfterNow = (monthDate - now) / (1000 * 60 * 60 * 24 * 30.44);
            dbh = knownDBH + monthsAfterNow * DBH_GROWTH_PER_MONTH;
          }
        } else {
          dbh = Math.max(SEEDLING_DBH, SEEDLING_DBH + monthsFromPlanting * DBH_GROWTH_PER_MONTH);
        }

        monthlySums[i] += calcCO2eFromDBH(dbh, wd, ff);
      });
    });

    const data = monthlySums.map(v => parseFloat(v.toFixed(2)));
    // ── O2 derived directly from each month's CO2e sum — never computed independently ──
    const o2Data = data.map(co2 => parseFloat((co2 * CO2_TO_O2_RATIO).toFixed(2)));

    const total = data[data.length - 1] || 0;
    const totalO2Val = o2Data[o2Data.length - 1] || 0;
    const withDBH = trees.filter(t =>
      t.dia_breast_height != null &&
      t.dia_breast_height !== "null" &&
      parseFloat(t.dia_breast_height) > 0
    ).length;

    setCo2ChartData({ labels, data, o2Data });
    setTotalCO2e(total);
    setTotalO2(totalO2Val);
    setTreesWithDBH(withDBH);
  };

  const processSpeciesData = (trees) => {
    if (!trees || trees.length === 0) {
      setSpeciesData({});
      setFilteredTreeMapData([]);
      return;
    }

    const speciesGroups = trees.reduce((acc, tree) => {
      // Use tree_species ID as fallback if species label not available
      // const species = tree.tree_species_label || tree.treeName || `Species-${tree.tree_species}` || 'Unknown Species';
      const species = getSpeciesLabel(tree);
      if (!acc[species]) {
        acc[species] = {
          count: 0,
          // Try multiple possible icon URL fields
          icon: tree.species_icon_url || tree.icon_url || tree.treeImageUrl || '/images/icon/leaf.png',
          speciesId: tree.tree_species
        };
      }
      acc[species].count++;
      return acc;
    }, {});
    setSpeciesData(speciesGroups);
    setFilteredTreeMapData(trees);
  };

  const getBase64ImageFromUrl = async (imageUrl) => {
    if (!imageUrl) return null;
    let url = imageUrl;
    if (url.startsWith('/')) {
      url = window.location.origin + url;
    }
    try {
      const res = await fetch(url, { method: 'GET', mode: 'cors' });
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn("Fetch failed for image base64, trying fallback:", url, error);
      return new Promise((resolve) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          try {
            resolve(canvas.toDataURL('image/jpeg'));
          } catch (err) {
            console.error("Canvas tainted for image:", url, err);
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;
      let currentY = 15;

      const addHeader = (titleText) => {
        doc.setFillColor(27, 67, 50);
        doc.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F');
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(titleText, margin + 5, currentY + 8);
        currentY += 18;
      };

      const checkPageBreak = (heightNeeded) => {
        if (currentY + heightNeeded > pageHeight - margin - 15) {
          doc.addPage();
          currentY = margin;
          return true;
        }
        return false;
      };

      // PAGE 1: TITLE & COVER IMAGE & DETAILS
      doc.setFillColor(27, 67, 50);
      doc.rect(margin, currentY, pageWidth - 2 * margin, 25, 'F');

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(200, 220, 200);
      doc.text('PROJECT CLIMATE IMPACT REPORT BY ARANYASETU', margin + 6, currentY + 8);

      const splitTitle = doc.splitTextToSize(project?.name || 'Project Details', pageWidth - 2 * margin - 12);
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(splitTitle, margin + 6, currentY + 16);
      currentY += 32;

      if (project?.project_cover_image) {
        const coverBase64 = await getBase64ImageFromUrl(project.project_cover_image);
        if (coverBase64) {
          doc.addImage(coverBase64, 'JPEG', margin, currentY, pageWidth - 2 * margin, 80, undefined, 'FAST');
          doc.setDrawColor(27, 67, 50);
          doc.setLineWidth(0.3);
          doc.rect(margin, currentY, pageWidth - 2 * margin, 80);
          currentY += 85;
        }
      }

      const detailsData = [
        ['Total Target', `${project?.target || 0} Trees`],
        ['Trees Planted', `${project?.total_trees_planted || 0} Trees`],
        ['Duration', `${project?.startDate || 'N/A'} To ${project?.endDate || 'N/A'}`],
        ['Companies', project?.organizationName || 'N/A'],
        ['Venue / Address', project?.address || 'N/A'],
        ['Tags', project?.tags ? project.tags.split(",").map(t => `#${t.trim()}`).join(", ") : 'N/A']
      ];

      autoTable(doc, {
        startY: currentY,
        body: detailsData,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 0,
          textColor: [60, 60, 60],
          font: 'Helvetica'
        },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 35 },
          1: { width: 55 },
          2: { fontStyle: 'bold', textColor: [27, 67, 50], width: 35 },
          3: { width: 55 },
          4: { fontStyle: 'bold', textColor: [27, 67, 50], width: 35 },
          5: { width: 55 },
          6: { fontStyle: 'bold', textColor: [27, 67, 50], width: 35 },
          7: { width: 55 },
          8: { fontStyle: 'bold', textColor: [27, 67, 50], width: 35 },
          9: { width: 55 },
          10: { fontStyle: 'bold', textColor: [27, 67, 50], width: 35 },
          11: { width: 55 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 15;

      // Focused SDG Goals (Only render if goals exist and are non-empty)
      if (project?.esgGoals && project.esgGoals.trim() !== "") {
        const goals = project.esgGoals.split(",").map(g => g.trim()).filter(Boolean);
        if (goals.length > 0) {
          checkPageBreak(35);
          addHeader('Focused SDG Goals');

          const goalImages = await Promise.all(
            goals.map(async (goal) => {
              const path = `/images/EsgGoals/${goal.replaceAll(" ", "-")}.jpg`;
              const base64 = await getBase64ImageFromUrl(path);
              return { goal, base64 };
            })
          );

          let startX = margin;
          let goalRowHeight = 22;
          let hasImages = false;

          goalImages.forEach((gData) => {
            if (gData.base64) {
              hasImages = true;
              if (startX + 22 > pageWidth - margin) {
                startX = margin;
                currentY += goalRowHeight + 4;
                checkPageBreak(goalRowHeight);
              }
              doc.addImage(gData.base64, 'JPEG', startX, currentY, 20, 20, undefined, 'FAST');
              startX += 25;
            }
          });

          if (hasImages) {
            currentY += goalRowHeight + 10;
          } else {
            currentY -= 18; // Rollback header spacing
          }
        }
      }

      // Project Description (Only render if non-empty)
      if (project?.description && project.description.trim() !== "") {
        checkPageBreak(30);
        addHeader('Project Description');

        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        const splitDesc = doc.splitTextToSize(project.description.trim(), pageWidth - 2 * margin);

        let linesLeft = splitDesc.length;
        let currentLineIndex = 0;

        while (linesLeft > 0) {
          const maxLinesOnPage = Math.floor((pageHeight - margin - 15 - currentY) / 5);
          if (maxLinesOnPage <= 3) {
            doc.addPage();
            currentY = margin;
            continue;
          }

          const linesToPrint = Math.min(linesLeft, maxLinesOnPage);
          const chunk = splitDesc.slice(currentLineIndex, currentLineIndex + linesToPrint);
          doc.text(chunk, margin, currentY);

          currentY += linesToPrint * 5;
          currentLineIndex += linesToPrint;
          linesLeft -= linesToPrint;

          if (linesLeft > 0) {
            doc.addPage();
            currentY = margin;
          }
        }
        currentY += 12;
      }

      // Total Cumulative CO2 Offset & O2 Produced by Month (Only render if there is data)
      const hasCO2Data = co2ChartRef.current || co2ChartData;
      if (hasCO2Data) {
        checkPageBreak(95);
        addHeader('Total Cumulative CO2 Offset & O2 Produced by Month');

        const summaryText = `Total CO2e Stored: ${totalCO2e >= 1000 ? (totalCO2e / 1000).toFixed(2) + ' t' : totalCO2e + ' kg'}   |   Total O2 Produced: ${totalO2 >= 1000 ? (totalO2 / 1000).toFixed(2) + ' t' : totalO2 + ' kg'}   |   Trees with DBH: ${treesWithDBH} / ${treeMapData.length}`;
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(27, 67, 50);
        doc.text(summaryText, margin, currentY);
        currentY += 6;

        if (co2ChartRef.current) {
          try {
            const co2Img = co2ChartRef.current.toBase64Image();
            if (co2Img) {
              doc.addImage(co2Img, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture CO2 Chart:", e);
            if (co2ChartData) {
              const tableRows = co2ChartData.labels.map((lbl, idx) => [lbl, `${co2ChartData.data[idx]} kg`, `${co2ChartData.o2Data[idx]} kg`]);
              autoTable(doc, {
                startY: currentY,
                head: [['Month', 'CO2e Absorbed', 'O2 Produced']],
                body: tableRows,
                margin: { left: margin, right: margin },
                styles: { fontSize: 8 },
                headStyles: { fillColor: [76, 175, 80] }
              });
              currentY = doc.lastAutoTable.finalY + 10;
            }
          }
        } else if (co2ChartData) {
          const tableRows = co2ChartData.labels.map((lbl, idx) => [lbl, `${co2ChartData.data[idx]} kg`, `${co2ChartData.o2Data[idx]} kg`]);
          autoTable(doc, {
            startY: currentY,
            head: [['Month', 'CO2e Absorbed', 'O2 Produced']],
            body: tableRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [76, 175, 80] }
          });
          currentY = doc.lastAutoTable.finalY + 10;
        }
        currentY += 5;
      }

      // Tree Growth (Only render if there is data)
      const hasGrowthData = growthChartRef.current || chartData;
      if (hasGrowthData) {
        checkPageBreak(95);
        addHeader('Tree Growth (Cumulative Trees Planted)');

        if (growthChartRef.current) {
          try {
            const growthImg = growthChartRef.current.toBase64Image();
            if (growthImg) {
              doc.addImage(growthImg, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture Growth Chart:", e);
            if (chartData) {
              const tableRows = chartData.labels.map((lbl, idx) => [lbl, chartData.datasets[0].data[idx]]);
              autoTable(doc, {
                startY: currentY,
                head: [['Date/Month', 'Trees Planted']],
                body: tableRows,
                margin: { left: margin, right: margin },
                styles: { fontSize: 8 },
                headStyles: { fillColor: [139, 195, 74] }
              });
              currentY = doc.lastAutoTable.finalY + 10;
            }
          }
        } else if (chartData) {
          const tableRows = chartData.labels.map((lbl, idx) => [lbl, chartData.datasets[0].data[idx]]);
          autoTable(doc, {
            startY: currentY,
            head: [['Date/Month', 'Trees Planted']],
            body: tableRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [139, 195, 74] }
          });
          currentY = doc.lastAutoTable.finalY + 10;
        }
        currentY += 5;
      }

      // Species-wise Tree Distribution (Only render if there is species rows)
      const speciesRows = Object.keys(speciesData).map(specKey => {
        return [specKey, speciesData[specKey].count];
      });

      if (speciesRows.length > 0) {
        checkPageBreak(40);
        addHeader('Species-wise Tree Distribution');

        autoTable(doc, {
          startY: currentY,
          head: [['Species Name', 'Tree Count']],
          body: speciesRows,
          theme: 'striped',
          styles: { fontSize: 9, font: 'Helvetica' },
          headStyles: { fillColor: [27, 67, 50], textColor: [255, 255, 255] },
          margin: { left: margin, right: margin }
        });
        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Project Gallery (Only render if gallery has images)
      if (images.length > 0) {
        checkPageBreak(55);
        addHeader('Project Gallery');

        const galleryB64 = await Promise.all(
          images.slice(0, 8).map(async (img) => {
            const b64 = await getBase64ImageFromUrl(img.src);
            return { b64, caption: img.caption || '' };
          })
        );

        let imgWidth = 53;
        let imgHeight = 35;
        let gap = 5;
        let xPos = margin;
        let hasGalleryImages = false;

        for (let i = 0; i < galleryB64.length; i++) {
          const imgObj = galleryB64[i];
          if (imgObj.b64) {
            hasGalleryImages = true;
            checkPageBreak(imgHeight + 12);
            if (xPos + imgWidth > pageWidth - margin) {
              xPos = margin;
              currentY += imgHeight + 12;
            }

            doc.addImage(imgObj.b64, 'JPEG', xPos, currentY, imgWidth, imgHeight, undefined, 'FAST');
            doc.setDrawColor(200, 200, 200);
            doc.rect(xPos, currentY, imgWidth, imgHeight);

            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(100, 100, 100);
            const truncCaption = doc.splitTextToSize(imgObj.caption, imgWidth);
            doc.text(truncCaption, xPos, currentY + imgHeight + 4.5);

            xPos += imgWidth + gap;
          }
        }
        if (hasGalleryImages) {
          currentY += imgHeight + 15;
        } else {
          currentY -= 18; // Rollback header spacing
        }
      }

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);

        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
        doc.text(`Aranyasetu Project: ${project?.name || 'Report'}`, margin, pageHeight - 8);

        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);
      }

      const pdfFileName = `${(project?.name || 'Project-Details').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_report.pdf`;
      doc.save(pdfFileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const groupByWeek = (trees) => {
    const weeklyData = {};
    trees.forEach((tree) => {
      if (!tree.plantedOn) return;
      const date = new Date(tree.plantedOn);
      const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
      const weekKey = weekStart.toISOString().split("T")[0];
      weeklyData[weekKey] = (weeklyData[weekKey] || 0) + 1;
    });
    return weeklyData;
  };

  const groupByMonth = (trees) => {
    const monthlyData = {};
    trees.forEach((tree) => {
      if (!tree.plantedOn) return;
      const date = new Date(tree.plantedOn);
      const monthKey = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
    });
    return monthlyData;
  };

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const response = await api.get(
          `api/project/getProjectGalleryDetails/${projectid}`
        );
        if (response.data.success) {
          setProjectData(response.data);

          // Handle gallery images
          const galleryImages = response.data.galleryImages || [];
          const formattedImages = galleryImages.map((image) => ({
            src: image.imageUrl || '',
            thumbnail: image.imageUrl || '',
            caption: image.imageDescription || image.imageCaption || '',
            thumbnailWidth: 320,
            thumbnailHeight: 212,
          }));
          setImages(formattedImages);

          // Handle trees data - filter out trees without coordinates
          const trees = response.data.Trees || [];
          const validTrees = trees.filter(tree =>
            tree.lat && (tree.lng || tree.longi) &&
            !isNaN(parseFloat(tree.lat)) &&
            !isNaN(parseFloat(tree.lng || tree.longi))
          );

          setTreeMapData(validTrees);
          processTreeData(validTrees);
          processSpeciesData(validTrees);

          // Handle project area GeoJSON if it exists
          const project = response.data.project;
          if (project && project.area_geojson) {
            try {
              const parsedGeoJson = JSON.parse(project.area_geojson);
              setGeoJsonData(parsedGeoJson);
            } catch (error) {
              console.error("Error parsing GeoJSON:", error);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };

    fetchProjectDetails();
  }, [projectid]);

  useEffect(() => {
    if (treeMapData.length > 0) {
      buildCO2ChartData(treeMapData);
    }
  }, [treeMapData]);

  const handleSpeciesFilter = (selectedSpecies) => {
    if (selectedSpecies === 'all') {
      setFilteredTreeMapData(treeMapData);
    } else {
      setFilteredTreeMapData(
        treeMapData.filter(tree => {
          // const species = tree.tree_species_label || tree.treeName || `Species-${tree.tree_species}` || 'Unknown Species';
          const species = getSpeciesLabel(tree);
          return species === selectedSpecies;
        })
      );
    }
  };

  // GeoJSON style options
  const geoJsonStyle = {
    fillColor: "#8BC34A",
    fillOpacity: 0.2,
    color: "#8BC34A",
    weight: 2,
  };

  // Function to fit map bounds to GeoJSON
  const onGeoJsonLoad = (e) => {
    const bounds = e.target.getBounds();
    e.target._map.fitBounds(bounds);
  };

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  useEffect(() => {
    if (isOpen && images.length > 0) {
      const img = new Image();
      img.src = images[currentImage].src;
      img.onload = handleImageLoad;
      img.onerror = () => {
        console.error("Error loading image:", images[currentImage].src);
        setIsImageLoaded(true); // Set to true even on error to remove loading state
      };
    } else {
      setIsImageLoaded(false);
    }
  }, [isOpen, currentImage, images, handleImageLoad]);

  const processTreeData = (trees) => {
    if (!trees || trees.length === 0) {
      setChartData(null);
      return;
    }

    // Filter trees with valid plantedOn dates
    const validTrees = trees.filter(tree => tree.plantedOn);

    if (validTrees.length === 0) {
      setChartData(null);
      return;
    }

    // Sort trees by plantedOn date
    const sortedTrees = validTrees.sort(
      (a, b) => new Date(a.plantedOn) - new Date(b.plantedOn)
    );

    const startDate = new Date(sortedTrees[0].plantedOn);
    const endDate = new Date(sortedTrees[sortedTrees.length - 1].plantedOn);
    const daysDifference = (endDate - startDate) / (1000 * 60 * 60 * 24);

    const isMonthly = daysDifference > 49; // If more than 7 weeks, use monthly view

    const groupedData = isMonthly
      ? groupByMonth(sortedTrees)
      : groupByWeek(sortedTrees);

    const labels = Object.keys(groupedData);
    const data = Object.values(groupedData);

    setChartData({
      labels,
      datasets: [
        {
          label: "Trees Planted",
          data,
          fill: false,
          backgroundColor: "#8BC34A",
          borderColor: "#8BC34A",
        },
      ],
    });
  };

  // Define the months dynamically if needed or keep them fixed
  const months = apiStats.map((stat) => stat.registration_month) || [
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
  ];

  const treeStatsData = {
    labels: months,
    datasets: [
      {
        label: "Total Trees Registered",
        borderColor: "#4CAF50",
        data: apiStats.map((stat) => stat.total_trees_registered),
        tension: 0.1,
      },
    ],
  };

  if (!projectData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
        <div style={{ marginLeft: '20px' }}>
          <p>Loading project details...</p>
        </div>
      </div>
    );
  }

  const { project } = projectData;


  // Get map center position from project coordinates or use default
  const mapCenter = project?.lat && (project?.lng || project?.longi)
    ? [parseFloat(project.lat), parseFloat(project.lng || project.longi)]
    : mockData.position;


  return (
    <div className="project-page">
      {isExporting && (
        <div className="pdf-export-overlay">
          <div className="pdf-loader-wrapper">
            <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
          </div>
          <p className="pdf-export-overlay-title">Generating Project PDF Report...</p>
          <p className="pdf-export-overlay-subtitle">Preparing charts, images, and species distribution details</p>
        </div>
      )}
      {project?.project_cover_image && (
        <div
          className="cover-image"
          // style={{
          //   backgroundImage: `url(${project.project_cover_image})`,
          //   height: "300px",
          // }}
          style={{
            backgroundImage: `url(${project.project_cover_image})`,
            width: "100%",
            height: "100%",
            paddingTop: "25%",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div>
      )}




      <div className="project-info-container">
        <div className="project-climate-report-heading">
          PROJECT CLIMATE IMPACT REPORT
        </div>

        <div className="project-header">
          <h3 className="project-name">{project?.name || 'Project Name Not Available'}</h3>


          <div className="tree-targets">
            <div className="target-btn">
              <FaBullseye />{" "}
              <span>
                <strong>Total Target: {project?.target || 0}</strong>
              </span>
            </div>
            <div className="target-btn">
              <FaTree />{" "}
              <span>
                <strong>Trees Planted: {project?.total_trees_planted || 0}</strong>
              </span>
            </div>
            <button
              className="export-pdf-btn"
              onClick={handleExportPDF}
              disabled={isExporting}
            >
              <FaFilePdf />{" "}
              <span>
                <strong style={{color:"#0c3292"}}>{isExporting ? "Exporting..." : "Export Report"}</strong>
              </span>
            </button>
          </div>
        </div>

        <div className="project-description">
          <p className="flex items-center" style={{ marginBottom: '-5px' }}>
            <FaRegClock className="mr-1 text-blue-600" />
            Duration: <span className="font-normal ml-1 text-black">
              {project?.startDate || 'N/A'} To {project?.endDate || 'N/A'}
            </span>
          </p>
          <p className="flex items-center" style={{ marginBottom: '-5px' }}>
            <FaBuilding className="mr-1 text-blue-600" />
            Participants: <span className="font-normal ml-1 text-black">
              {project?.organizationName || 'N/A'}
            </span>
          </p>
          <p className="flex items-center" style={{ marginBottom: '-5px' }}>
            <FaMapMarkerAlt className="mr-1 text-blue-600" />
            Venue: <span className="font-normal ml-1 text-black">
              {project?.address || 'N/A'}
            </span>
          </p>
          <p className="flex items-center" style={{ marginBottom: '-5px' }}>
            <FaHashtag className="mr-1 text-blue-600" />
            Tags: <span className="font-normal ml-1 text-black">
              {project?.tags ? project.tags.split(",").map((tag) => ` #${tag.trim()}`).join(",") : 'N/A'}
            </span>
          </p>

          {project?.esgGoals && (
            <>
              <h3 className="project-name" style={{ marginTop: '10px' }}>Focused SDG Goals </h3>
              <div
                className="share-buttons flex flex-wrap items-center gap-2 pt-2 pb-2"
                style={{ justifyContent: "flex-start" }}
              >
                {project.esgGoals.split(",").map((goal) => (
                  <img
                    key={goal.trim()}
                    src={`/images/EsgGoals/${goal.trim().replaceAll(" ", "-")}.jpg`}
                    alt={goal.trim()}
                    className="h-20 w-20 object-contain"
                    data-tooltip-id="Esg-Goals"
                    data-tooltip-content={goal}
                  />
                ))}
                <Tooltip id="Esg-Goals" place="top" effect="solid" />
              </div>
            </>
          )}

          <div
            className="share-buttons"
            style={{ justifyContent: "flex-start" }}
          >
            <FacebookShareButton url={window.location.href}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={window.location.href}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => {
                    const shareUrl = `https://www.instagram.com/`;
                    window.open(shareUrl, "_blank");
                  })
                  .catch((err) => {
                    console.error("Failed to copy: ", err);
                  });
              }}
              style={{
                height: "32px",
                width: "32px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                padding: "5px",
                backgroundColor: "#D93F7C",
              }}
            >
              <FaInstagram size={32} />
            </button>
            <WhatsappShareButton url={window.location.href}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={window.location.href}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
            <EmailShareButton url={window.location.href}>
              <EmailIcon size={32} round />
            </EmailShareButton>
          </div>

          {project?.description && (
            <>
              <h3 className="project-name" style={{ marginTop: "10px" }}>
                Project Description
              </h3>
              <span className="text-black"
                style={{ marginTop: '0px' }}
                dangerouslySetInnerHTML={{
                  __html: project.description.trim().replace(/\n+/g, "<br/>"),
                }}
              />
            </>
          )}
        </div>
      </div>

      <div className="content-section">
        {isOpen && images.length > 0 && (
          <Lightbox
            mainSrc={images[currentImage].src}
            nextSrc={images[(currentImage + 1) % images.length].src}
            prevSrc={images[(currentImage + images.length - 1) % images.length].src}
            onCloseRequest={() => setIsOpen(false)}
            onMovePrevRequest={() => {
              setCurrentImage((currentImage + images.length - 1) % images.length);
              setIsImageLoaded(false);
            }}
            onMoveNextRequest={() => {
              setCurrentImage((currentImage + 1) % images.length);
              setIsImageLoaded(false);
            }}
            imageCaption={images[currentImage].caption}
            onImageLoad={handleImageLoad}
            reactModalStyle={{
              overlay: { zIndex: 1500 },
            }}
          />
        )}

        {/* Flex container for equal width graphs */}
        <div style={{ display: "flex", gap: "10px", width: "100%" }}>
          <div
            className="dashboard-card co2-offset-by-month"
            style={{ flex: 1, padding: "20px" }}
          >
            <h3 className="project-name" style={{ marginBottom: "6px" }}>
              Total Cumulative CO2 Offset &amp; O2 Produced by Month
            </h3>

            {co2ChartData ? (
              <>
                {/* Summary strip */}
                <div style={{ display: 'flex', gap: '20px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Total CO2e Stored </p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#4CAF50' }}>
                      {totalCO2e >= 1000
                        ? `${(totalCO2e / 1000).toFixed(2)} t`
                        : `${totalCO2e} kg`}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Total O2 Produced </p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#3B82F6' }}>
                      {totalO2 >= 1000
                        ? `${(totalO2 / 1000).toFixed(2)} t`
                        : `${totalO2} kg`}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Trees with DBH</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#333' }}>
                      {treesWithDBH}
                      <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
                        {' '}/ {treeMapData.length} total
                      </span>
                    </p>
                  </div>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4CAF50', display: 'inline-block' }} />
                    <span style={{ fontSize: '11px', color: '#888' }}>CO2e Absorbed</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3B82F6', display: 'inline-block' }} />
                    <span style={{ fontSize: '11px', color: '#888' }}>O2 Produced</span>
                  </div>
                </div>

                <Line
                  ref={co2ChartRef}
                  data={{
                    labels: co2ChartData.labels,
                    datasets: [
                      {
                        label: "CO2e (kg)",
                        data: co2ChartData.data,
                        borderColor: "#4CAF50",
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                        pointBackgroundColor: "#4CAF50",
                      },
                      {
                        label: "O2 (kg)",
                        data: co2ChartData.o2Data,
                        borderColor: "#3B82F6",
                        backgroundColor: "rgba(59, 130, 246, 0.08)",
                        fill: true,
                        tension: 0.3,
                        borderDash: [4, 3],
                        pointRadius: 2,
                        pointBackgroundColor: "#3B82F6",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const val = ctx.parsed.y;
                            const unit = val >= 1000 ? `${(val / 1000).toFixed(2)} t` : `${val} kg`;
                            return ` ${ctx.dataset.label.replace(' (kg)', '')}: ${unit}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: { display: true, text: "kg" }
                      },
                      x: {
                        title: { display: true, text: "Month" },
                        ticks: { maxTicksLimit: 12 }
                      }
                    }
                  }}
                />
                <p style={{ fontSize: '10px', color: '#aaa', marginTop: '6px' }}>
                  Biomass Formula Method (Chave et al. 2014) · Trees without DBH estimated at +0.05 cm/month · O2 = CO2e × 0.727
                </p>
              </>
            ) : (
              <p style={{ color: '#aaa', textAlign: 'center', paddingTop: '40px' }}>
                No planting data available to calculate CO2e
              </p>
            )}
          </div>

          <div className="dashboard-card co2-offset-by-month"
            style={{ flex: 1, padding: "20px" }}
          >
            <h3 className="project-name" style={{ marginBottom: "10px" }}>
              Tree Growth
            </h3>
            {chartData ? (
              <Line ref={growthChartRef} data={chartData} options={{
                responsive: true,
                plugins: {
                  legend: { display: false },
                  title: { display: false },
                },
                scales: { y: { beginAtZero: true } },
              }} />
            ) : (
              <p>No tree planting data available</p>
            )}
          </div>
        </div>
      </div>

      <div className="project-info-container" >
        <h3 className="project-name" style={{ marginBottom: '10px' }}>Project Images</h3>
        {images.length > 0 ? (
          <Gallery
            thumbnailStyle={() => ({
              border: "1px solid black",
              height: "100%",
              width: "100%",
            })}
            images={images}
            enableImageSelection={false}
            onClick={(index) => {
              setCurrentImage(index);
              setIsOpen(true);
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <p className="text-gray-500">No Project images available</p>
            <img src={noImage} alt="No image" className="w-24 h-24 object-contain opacity-70" />
          </div>
        )}
      </div>

      {treeMapData.length > 0 && (
        <div className="Map-Container p-2">
          <h2 className="p-2 text-center"> Interactive Diversity Map</h2>
          <MapContainer
            center={mapCenter}
            zoom={4}
            style={{ height: "100vh", width: "100%" }}
          >
            <MapSpeciesFilter
              speciesData={speciesData}
              onFilterChange={handleSpeciesFilter}
            />
            <TileLayer url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" />
            {geoJsonData && (
              <GeoJSON
                data={geoJsonData}
                style={geoJsonStyle}
                onEachFeature={(feature, layer) => {
                  layer.on({
                    load: onGeoJsonLoad,
                  });
                }}
              />
            )}
            <MapToggle />
            <CustomClusterGroup
              trees={filteredTreeMapData}
              speciesData={speciesData}
            />
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default ProjectPage;